import type { ChapterResponse } from '@goldcast/api/content';
import { ClipMetadata } from '@goldcast/player-config/src/types/clip-types';
import createStore from './store';
import appLogger from '@/services/logger';
import { ClipVersion } from '@/Pages/Clip/ClipPlayer/ClipCustomizer/ClipCustomizerVersioningPanel/types';

interface ClipVersionState {
  versions: ClipVersion[];
  viewingVersion: ClipVersion | null;
  /** Which clip view mode was entered for — used to drop view mode when the clip changes. */
  viewingClipId: string | null;
  /**
   * True only while the initial list for a clip is in flight. Background refreshes (after a
   * save or restore) deliberately leave it false so the list doesn't flash back to skeletons.
   */
  isLoadingVersions: boolean;
  /**
   * The version currently being restored, or null. Lives in the store because the panel and
   * the view-mode banner both render a restoring state and must agree on it.
   */
  restoringVersion: ClipVersion | null;
}

const initialState: ClipVersionState = {
  versions: [],
  viewingVersion: null,
  viewingClipId: null,
  isLoadingVersions: false,
  restoringVersion: null
};

const store = createStore<ClipVersionState>(initialState);

type EditEventListener = (clipId: string, updateType: string) => void;
const editListeners = new Set<EditEventListener>();

export type HistoryStepDirection = 'undo' | 'redo';
type HistoryStepListener = (clipId: string, direction: HistoryStepDirection) => void;
const historyStepListeners = new Set<HistoryStepListener>();

export interface PreViewSnapshot {
  clipId: string;
  metadata: ClipMetadata;
  /** Chapters live on the broadcast, so they are captured separately from the metadata. */
  chapters: ChapterResponse[] | undefined;
}

/**
 * The live editable state captured before entering view mode, so Exit can
 * return to the exact pre-view state — including edits made since the last version.
 * Deliberately outside the reactive state: writing it must not trigger a re-render,
 * and it must survive individual hook instances unmounting.
 */
let preViewSnapshot: PreViewSnapshot | null = null;

export const clipVersionStore = {
  ...store,
  setVersions: (versions: ClipVersion[]) =>
    store.update(draft => {
      draft.versions = versions;
    }),
  setVersionsLoading: (isLoading: boolean) =>
    store.update(draft => {
      draft.isLoadingVersions = isLoading;
    }),
  setRestoringVersion: (version: ClipVersion | null) =>
    store.update(draft => {
      draft.restoringVersion = version;
    }),
  setViewingVersion: (version: ClipVersion | null, clipId: string | null = null) =>
    store.update(draft => {
      draft.viewingVersion = version;
      draft.viewingClipId = version ? clipId : null;
    }),
  /**
   * True while this clip is showing a previewed version rather than the user's live work.
   * Edit paths check this before mutating, so an edit made in view mode cannot be saved as a
   * hybrid of the previewed state and the new change.
   */
  isPreviewing: (clipId: string): boolean => store.getSnapshot().viewingClipId === clipId,
  reset: () => {
    preViewSnapshot = null;
    store.set(() => initialState);
  },
  /** Store the live state captured before entering view mode. */
  setPreViewSnapshot: (clipId: string, metadata: ClipMetadata, chapters: ChapterResponse[] | undefined) => {
    preViewSnapshot = { clipId, metadata, chapters };
  },
  /** Return the snapshot for this clip and clear it. Returns null if none is held. */
  takePreViewSnapshot: (clipId: string): PreViewSnapshot | null => {
    if (preViewSnapshot?.clipId !== clipId) {
      return null;
    }
    const snapshot = preViewSnapshot;
    preViewSnapshot = null;
    return snapshot;
  },
  /**
   * Called from edit flows to signal a real edit occurred.
   * Listener failures are contained: this runs inside template apply, overlay add,
   * transcript edit and other core paths, so a fault in versioning must never
   * propagate into the edit the user is performing.
   */
  emitEdit: (clipId: string, updateType: string) => {
    editListeners.forEach(fn => {
      try {
        fn(clipId, updateType);
      } catch (err) {
        appLogger.error('[versioning] edit listener failed', err);
      }
    });
  },
  /** Returns an unsubscribe function. */
  onEdit: (fn: EditEventListener) => {
    editListeners.add(fn);
    return () => {
      editListeners.delete(fn);
    };
  },
  /** Signals an undo/redo step — not a new edit, but one taken back or reinstated. */
  emitHistoryStep: (clipId: string, direction: HistoryStepDirection) => {
    historyStepListeners.forEach(fn => {
      try {
        fn(clipId, direction);
      } catch (err) {
        appLogger.error('[versioning] history step listener failed', err);
      }
    });
  },
  /** Returns an unsubscribe function. */
  onHistoryStep: (fn: HistoryStepListener) => {
    historyStepListeners.add(fn);
    return () => {
      historyStepListeners.delete(fn);
    };
  }
};
